<template>
	<figure v-if="image" class="wp-image-vue" :class="classes">
		<template v-if="background">
			<template v-if="lazy">
				<div class="image item" :data-bg="'url(' + imageUrl + ')'"></div>
				<div
					v-if="placeholderImage !== imageUrl"
					class="image image-placehold"
					:style="'background-image:url(' + placeholderImage + ')'"
				></div>
			</template>
			<template v-else>
				<div class="image" :style="'background-image:url(' + imageUrl + ')'"></div>
			</template>
		</template>
		<template v-else>
			<img :alt="imgAlt" class="image item" :src="placeholderImage" :data-src="imageUrl" />
		</template>
		<noscript>
			<img :alt="imgAlt" class="image item" :src="imageUrl" />
		</noscript>
		<figcaption class="description" v-if="image && ((image.caption && caption) || (image.title && title))">
			<span v-if="title" class="title">{{ image.title }}</span>
			<span v-if="caption" class="caption">{{ image.caption }}</span>
		</figcaption>
	</figure>
</template>

<script>
import shortid from 'shortid'
import LazyLoad from 'vanilla-lazyload'

/**
 * Support Sizes:
 * medium  300w
 * large 1024w
 * thumbnail 150w
 * medium_large 768w
 * min 32w
 * extra_large 1920px
 * full - full image size, that was uploaded
 */
export default {
	name: 'wp-image',
	props: {
		url: String,
		alt: String,
		delay: {
			//delay for lazy load
			type: Number,
			default: 50,
		},
		size: {
			type: String,
			default: 'medium',
		},
		noPadding: {
			default: false,
			type: Boolean,
		},
		image: {
			type: Object,
		},
		title: {
			default: false,
			type: Boolean,
		},
		caption: {
			default: false,
			type: Boolean,
		},
		styledCaption: {
			default: true,
			type: Boolean,
		},
		lazy: {
			default: true,
			type: Boolean,
		},
		background: {
			default: false,
			type: Boolean,
		},
		cover: {
			default: false,
			type: Boolean,
		},
		prop: {
			default: '16x9',
			type: String,
		},
	},
	data() {
		return {
			lazyLoader: null,
			//Fix missing page url for images
			devUrl:
				process.env.NODE_ENV === 'development' && window.WPVUE && window.WPVUE.siteurl
					? window.WPVUE.siteurl.replace(/\/$/, '')
					: '',
		}
	},
	computed: {
		uniqImageClass() {
			return 'image-' + shortid.generate()
		},
		imgAlt() {
			let alt = this.alt
			if (!alt && this.image) {
				return this.image.alt
			}
			return alt
		},
		classes() {
			let custom = [this.uniqImageClass]
			if (this.cover) custom.push('cover')
			if (this.lazy) custom.push('lazy')
			if (this.noPadding) custom.push('image-no-padding')
			if (this.background) custom.push('background')
			if (!this.background) custom.push('img')
			if (this.styledCaption) custom.push('styled-caption')
			if (this.size) custom.push(this.size)
			return custom.splice(' ')
		},
		placeholderImage() {
			const placehold = this.image && this.image.sizes && this.image.sizes.min ? this.image.sizes.min.url : ''
			let placeholderUrl = this.lazy ? placehold : this.imageUrl
			if (this.devUrl && !placeholderUrl.includes(this.devUrl)) placeholderUrl = this.devUrl + placeholderUrl

			return placeholderUrl
		},
		imageUrl() {
			const VM = this
			let imageUrl = ''
			if (VM.url && VM.url !== '') {
				imageUrl = VM.url
			} else {
				const size = VM.size || false
				imageUrl = VM.image.url
				if (size && typeof VM.image.sizes[size] !== 'undefined' && VM.image.sizes[size]) {
					imageUrl = VM.image.sizes[size]['url']
				}
			}

			if (VM.devUrl && !imageUrl.includes(VM.devUrl)) imageUrl = VM.devUrl + imageUrl
			return imageUrl
		},
	},
	methods: {
		lazyLoaderInit() {
			const VM = this
			if (VM.lazy)
				VM.lazyLoader = new LazyLoad({
					elements_selector: '.' + VM.uniqImageClass + ' .item',
					load_delay: VM.delay,
				})
		},
		lazyLoaderDestroy() {
			if (this.lazy && this.lazyLoader) this.lazyLoader.destroy()
		},
	},
	mounted() {
		this.lazyLoaderInit()
	},
	updated() {
		this.$nextTick(() => {
			if (this.lazy && this.lazyLoader) {
				this.lazyLoader.update()
			}
		})
	},
	beforeDestroy() {
		this.lazyLoaderDestroy()
	},
}
</script>

<style lang="scss">
@import '@/assets/vars.scss';
@import '@/assets/mixins.scss';
.wp-image-vue {
	margin: 0;
	position: relative;
	overflow: hidden;
	//border-radius: $blocksRadius;
	&.background {
		&.styled-caption {
			.description {
				color: var(--dark);
				font-weight: 400;
				text-decoration: none;
				margin-top: 25px;
				@include fluid-type($smallResText, $wideResText, 12px, 14px);
			}
		}
		div.image {
			width: 100%;
			padding-bottom: 56.4%;
			height: 0;
			background-position: center center;
			z-index: 2;
		}
		&.lazy div.image {
			transition: opacity 0.15s ease-in-out;
			will-change: opacity;
			&.item {
				opacity: 0;
				&[data-was-processed='true'] {
					opacity: 1;
					~ .image-placehold {
						opacity: 0;
					}
				}
			}
		}
		div.image-placehold {
			filter: blur(4px);
			position: absolute;
			top: 0px;
			left: 0px;
			bottom: 0px;
			right: 0px;
			z-index: -1;
		}
		&.cover div.image {
			background-size: cover;
		}
	}
	&.image-no-padding {
		div.image {
			padding-bottom: 0;
			height: 100%;
		}
	}
	&.img {
		&.lazy {
			img.image {
				opacity: 0;
				transition: opacity 0.15s ease-in-out;
				will-change: opacity;
				&.loaded {
					opacity: 1;
				}
			}
		}
	}
}
</style>
